import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Activity,
  BarChart3,
  AlertTriangle,
  Cpu,
  FileDown,
  LogOut,
  Database,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/monitor", label: "실시간 모니터링", icon: Activity },
  { to: "/index-admin", label: "검색 관리", icon: Database },
  { to: "/stats", label: "통계 / 보고서", icon: BarChart3 },
  { to: "/unanswered", label: "미답변 질문", icon: AlertTriangle },
  { to: "/reports", label: "내보내기", icon: FileDown },
  { to: "/announcements", label: "공지 관리", icon: Megaphone },
  { to: "/models", label: "모델 관리", icon: Cpu },
];

// 사이드바 접힘 상태 저장 키 — 새로고침/재방문에도 유지
const COLLAPSE_KEY = "arkiveai.admin.sidebarCollapsed";

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

export function Layout() {
  const { state, logout } = useAuth();
  const user = state.status === "authenticated" ? state.user : null;
  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* localStorage 불가 환경(사생활 보호 모드 등) — 상태만 토글 */
      }
      return next;
    });
  };

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col border-r bg-card transition-[width] duration-200",
          collapsed ? "w-16" : "w-64",
        )}
      >
        <div
          className={cn(
            "flex h-14 items-center border-b",
            collapsed ? "justify-center px-2" : "justify-between pl-5 pr-3",
          )}
        >
          {!collapsed && (
            <span className="overflow-hidden whitespace-nowrap text-lg font-bold tracking-tight">
              ArkiveAI <span className="text-primary">운영 포털</span>
            </span>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "메뉴 펼치기" : "메뉴 접기"}
            aria-label={collapsed ? "메뉴 펼치기" : "메뉴 접기"}
            aria-expanded={!collapsed}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>
        <nav className={cn("flex-1 space-y-1 p-3", collapsed && "px-2")}>
          {NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors",
                  collapsed ? "justify-center px-2" : "px-3",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )
              }
            >
              <it.icon className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <span className="overflow-hidden whitespace-nowrap">{it.label}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className={cn("border-t p-3", collapsed && "px-2")}>
          {user && !collapsed && (
            <div className="mb-2 px-2 text-xs">
              <div className="font-medium text-foreground">{user.display_name ?? user.email}</div>
              <div className="text-muted-foreground">{user.email}</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            title={collapsed ? "로그아웃" : undefined}
            className={cn("w-full gap-2", collapsed ? "justify-center px-0" : "justify-start")}
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && "로그아웃"}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
